import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DraftDocItem } from '@/services/counseling';
import { ConfirmButton } from '../components/fields';
import { useLoad } from '../hooks/useLoad';
import { useService } from '../hooks/useService';
import { CreatedAt } from '../components/CreatedAt';

const DOC_NAME: Record<string, string> = {
  session_record: '상담기록지',
  case_report: '사례보고서',
  termination_report: '종결보고서',
  verbatim: '축어록',
  dialogue: '대화록',
};

/**
 * 홈 (UI 개편): 이어서 할 것 하나를 크게, 그 밖의 할 일, 내담자 카드(진행률·치료 방향).
 * PC에서는 오른쪽에 확인할 것·최근 완료.
 */
export function HomeScreen() {
  const svc = useService();
  const { data, error, reload } = useLoad(
    async () => ({ home: await svc.home(), cards: await svc.clientCards(), drafts: await svc.draftDocuments() }),
    [svc],
  );
  const today = new Date().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
  const home = data?.home;
  const [first, ...restPending] = home?.pending ?? [];
  const missedCards = (data?.cards ?? []).filter((c) => c.missed > 0 && c.next && c.clientId !== first?.clientId);
  const todo = (home?.pending.length ?? 0) + (home?.prepare.length ?? 0) + missedCards.length;

  return (
    <section className="home-grid">
      <div className="stack">
        <div className="page-head">
          <div>
            <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
              <h1 className="eyebrow">홈</h1>
              <span className="eyebrow">· {today}</span>
            </div>
            <p className="page-title">{data ? (todo ? `오늘 할 일 ${todo}개` : '밀린 일이 없어요') : '불러오는 중…'}</p>
          </div>
          <Link className="button" to="/sessions/new">
            새 회기 처리
          </Link>
        </div>
        {error && <p role="alert">불러오지 못했습니다.</p>}
        {home && home.duplicates.length > 0 && (
          <div className="card warn-card" role="alert">
            <h2>확인이 필요한 중복</h2>
            <p className="muted small">
              두 기기에서 따로 만든 기록이 동기화되어 같은 번호·코드가 두 개 있습니다. 자동으로 합치지 않았습니다.
            </p>
            <ul className="list">
              {home.duplicates.map((d) => (
                <li key={d.ids.join()} className="list-row">
                  <span>
                    {d.kind === 'session'
                      ? `${d.clientCode} ${d.sessionNumber}회기가 ${d.ids.length}개`
                      : `내담자 코드 ${d.clientCode}가 ${d.ids.length}개`}
                  </span>
                  <Link to={`/clients/${d.clientId}`}>확인</Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {first && (
          <Link className="hero-card" to={`/sessions/${first.sessionId}/run`}>
            <span className="hero-sub">이어서 하기</span>
            <span className="hero-title">
              {first.clientCode} · {first.sessionNumber}회기
            </span>
            <span className="hero-sub">
              {first.stage} · {first.sessionDate}
            </span>
            <span className="hero-go">계속하기 →</span>
          </Link>
        )}

        {(restPending.length > 0 || (home?.prepare.length ?? 0) > 0 || missedCards.length > 0) && (
          <div className="list-card">
            {restPending.map((p) => (
              <Link key={p.sessionId} className="list-item" to={`/sessions/${p.sessionId}/run`}>
                <span className="li-main">
                  <span className="li-title">
                    {p.clientCode} / {p.sessionNumber}회기
                  </span>
                  <span className="li-sub">
                    {p.sessionDate} · {p.stage} · <CreatedAt iso={p.createdAt} />
                  </span>
                </span>
                <span className="li-go">{p.action} →</span>
              </Link>
            ))}
            {home?.prepare.map((c) => (
              <Link key={c.briefId} className="list-item" to={`/clients/${c.clientId}/brief`}>
                <span className="li-main">
                  <span className="li-title">
                    {c.clientCode} · 다음 {c.targetSessionNumber}회기{' '}
                    {c.hasRiskFollowUp && <span className="status-pill warn">위험 확인</span>}
                  </span>
                  <span className="li-sub">{c.objectives[0] ?? `지난 회기 ${c.lastSessionDate ?? '-'}`}</span>
                </span>
                <span className="li-go">회기 준비 보기 →</span>
              </Link>
            ))}
            {missedCards.map((c) => (
              <Link key={c.clientId} className="list-item" to={c.next!.path}>
                <span className="li-main">
                  <span className="li-title">
                    {c.clientCode} · 놓친 단계 {c.missed}
                  </span>
                  <span className="li-sub">{c.next!.label}</span>
                </span>
                <span className="li-go">바로 가기 →</span>
              </Link>
            ))}
          </div>
        )}

        <div className="page-head" style={{ marginTop: 8 }}>
          <h2 style={{ margin: 0 }}>내담자</h2>
          <Link to="/clients">전체 보기</Link>
        </div>
        {data && data.cards.length === 0 && (
          <p className="muted">
            아직 내담자가 없습니다. <Link to="/clients">내담자 등록</Link>
          </p>
        )}
        <div className="grid-cards">
          {data?.cards.map((c) => (
            <Link key={c.clientId} className="card client-card" to={`/clients/${c.clientId}`}>
              <span className="row" style={{ justifyContent: 'space-between', width: '100%' }}>
                <strong>{c.clientCode}</strong>
                <span className="muted small">
                  {c.sessionCount}회기{c.status === 'terminated' ? ' · 종결' : c.status === 'paused' ? ' · 보류' : ''}
                </span>
              </span>
              <span className="meter" style={{ width: '100%' }}>
                <span style={{ width: `${c.pct}%` }} />
              </span>
              <span className={c.missed ? 'small warn' : 'muted small'}>
                {c.missed ? `놓친 단계 ${c.missed} · ${c.treatment}` : c.treatment}
              </span>
            </Link>
          ))}
        </div>
      </div>

      <aside className="stack home-side">
        {data && <DraftDocs items={data.drafts.items} total={data.drafts.total} onChanged={reload} />}
        <div className="list-card">
          <h2 style={{ margin: '12px 0 4px' }}>최근 완료</h2>
          {home && home.recent.length === 0 && <p className="muted small">아직 없습니다.</p>}
          {home?.recent.map((r) => (
            <Link key={r.sessionId} className="list-item" to={`/sessions/${r.sessionId}`}>
              <span className="li-main">
                <span className="li-title">
                  {r.clientCode} / {r.sessionNumber}회기
                </span>
                <span className="li-sub">
                  {r.sessionDate} · <CreatedAt iso={r.createdAt} />
                </span>
              </span>
              <span className="li-go">보기</span>
            </Link>
          ))}
        </div>
      </aside>
    </section>
  );
}

/**
 * 승인하지 않은 문서. 문서는 승인해야 "다 됨"이 되므로(Word로 받아도 초안), 여기서 바로 승인하거나 지울 수 있게 한다.
 * 지운 문서는 되돌릴 수 있다.
 */
function DraftDocs({ items, total, onChanged }: { items: DraftDocItem[]; total: number; onChanged: () => void }) {
  const svc = useService();
  const [undo, setUndo] = useState<{ id: string; name: string } | null>(null);
  if (!items.length && !undo) return null;
  const name = (d: DraftDocItem) =>
    `${d.clientCode} · ${d.sessionNumber ? `${d.sessionNumber}회기 ` : ''}${DOC_NAME[d.documentType] ?? d.documentType}`;
  return (
    <div className="list-card">
      <h2 style={{ margin: '12px 0 4px' }}>승인 안 한 문서 {total ? `(${total})` : ''}</h2>
      <p className="muted small" style={{ margin: 0 }}>
        문서는 승인해야 끝난 것으로 쳐요 (Word로 받아도 초안). 다 쓴 문서는 승인, 필요 없는 건 지우세요.
      </p>
      {undo && (
        <p className="small" role="status">
          "{undo.name}"을 지웠어요.{' '}
          <button
            type="button"
            className="link"
            onClick={() =>
              void svc.restoreDocument(undo.id).then(() => {
                setUndo(null);
                onChanged();
              })
            }
          >
            되돌리기
          </button>
        </p>
      )}
      {items.map((d) => (
        <div key={d.id} className="list-item draft-doc">
          <Link className="li-main" to={`/clients/${d.clientId}/documents?doc=${d.id}`}>
            <span className="li-title">{name(d)}</span>
            <span className="li-sub">고친 날 {d.updatedAt.slice(0, 10)} · 초안 · 열기</span>
          </Link>
          <span className="row draft-doc-actions">
            <ConfirmButton
              className="pill-btn"
              label="승인"
              confirmText="내용을 확인했나요? 승인하면 잠기고 [AI 초안] 표시가 지워져요."
              onConfirm={() => void svc.approveDocument(d.id).then(onChanged)}
            />
            <ConfirmButton
              className="link small"
              label="지우기"
              confirmText="이 초안을 지울까요? (바로 되돌릴 수 있어요)"
              onConfirm={() =>
                void svc.deleteDocument(d.id).then(() => {
                  setUndo({ id: d.id, name: name(d) });
                  onChanged();
                })
              }
            />
          </span>
        </div>
      ))}
      {total > items.length && (
        <p className="muted small" style={{ margin: '6px 0 0' }}>
          그 밖에 {total - items.length}개는 각 내담자의 문서 탭에 있어요.
        </p>
      )}
    </div>
  );
}
